import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles, Send, AlertCircle, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';
import TokenPurchaseModal from './TokenPurchaseModal';

interface AIAnalyzerModalProps {
  isOpen: boolean;
  onClose: () => void;
  dashboardId: string;
  dashboardName: string;
  dashboardData: {
    reports: any[];
    dateRange: string;
    customStartDate?: Date;
    customEndDate?: Date;
  };
  businessId: string;
  onFetchDataForDateRange: (dateRange: string, customStart?: Date, customEnd?: Date) => Promise<any>;
  availableReports: any[]; // All reports available in the library
}

interface AIMessage {
  role: 'user' | 'assistant';
  content: string;
  insights?: {
    type: 'positive' | 'warning' | 'negative';
    message: string;
  }[];
}

export default function AIAnalyzerModal({
  isOpen,
  onClose,
  dashboardId,
  dashboardName,
  dashboardData,
  businessId,
  onFetchDataForDateRange,
  availableReports
}: AIAnalyzerModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [conversation, setConversation] = useState<AIMessage[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTokenPurchaseModal, setShowTokenPurchaseModal] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setConversation([{
        role: 'assistant',
        content: `Hi! I'm your AI business analyst. I have access to all the data on your "${dashboardName}" dashboard. You can ask me:\n\n• How are my numbers looking?\n• Do you see any concerning trends?\n• Compare this month to last month\n• Give me recommendations to improve sales\n• Are there any red flags I should know about?\n\nWhat would you like to know?`
      }]);
    } else {
      setTimeout(() => {
        setIsAnimating(false);
        setConversation([]);
        setPrompt('');
        setError(null);
      }, 200);
    }
  }, [isOpen, dashboardName]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation]);

  const handleSubmit = async (e?: React.FormEvent, additionalData?: any) => {
    e?.preventDefault();
    if (!prompt.trim() && !additionalData && isAnalyzing) return;

    const userMessage = additionalData ? '' : prompt.trim();
    if (!additionalData) setPrompt('');
    setError(null);

    if (!additionalData) {
      setConversation(prev => [...prev, { role: 'user', content: userMessage }]);
    }
    setIsAnalyzing(true);

    try {
      // Get current date in user's local timezone
      const now = new Date();
      const currentDateInfo = {
        date: now.toISOString().split('T')[0], // YYYY-MM-DD
        year: now.getFullYear(),
        month: now.toLocaleString('default', { month: 'long' }),
        day: now.getDate(),
        fullDate: now.toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        })
      };

      // First call: Ask AI what data it needs (if not already provided)
      const { data, error: invokeError } = await supabase.functions.invoke('ai-analyze-kpi', {
        body: {
          prompt: userMessage,
          conversationHistory: conversation,
          businessId: selectedBusiness?.id,
          businessName: selectedBusiness?.business_name,
          dashboardId,
          dashboardName,
          dashboardData,
          availableReports, // Pass the reports library
          currentDateInfo, // Pass current date from user's timezone
          additionalData, // Pass any additional fetched data
        }
      });

      if (data?.insufficientTokens) {
        setShowTokenPurchaseModal(true);
        setError('Insufficient tokens. Please purchase more to continue.');
        setIsAnalyzing(false);
        return;
      }

      if (invokeError) throw invokeError;

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to analyze dashboard');
      }

      // Check if AI needs specific date range data
      if (data.needsDateRanges && Array.isArray(data.requestedDateRanges)) {
        console.log('🔄 AI requested date ranges:', data.requestedDateRanges);
        
        // Fetch all requested date ranges
        const fetchedData = await Promise.all(
          data.requestedDateRanges.map(async (rangeRequest: any) => {
            // Handle both string format (legacy) and object format (custom dates)
            const isCustomRange = typeof rangeRequest === 'object' && rangeRequest.type === 'custom';
            const dateRangeKey = isCustomRange ? 'custom' : (typeof rangeRequest === 'string' ? rangeRequest : rangeRequest.type);
            
            console.log(`📊 Fetching data for: ${isCustomRange ? rangeRequest.label : dateRangeKey}`);
            
            // For custom ranges, pass the dates
            const rangeData = await onFetchDataForDateRange(
              dateRangeKey,
              isCustomRange ? new Date(rangeRequest.startDate) : undefined,
              isCustomRange ? new Date(rangeRequest.endDate) : undefined
            );
            
            return {
              dateRange: isCustomRange ? rangeRequest.label : dateRangeKey,
              type: dateRangeKey,
              startDate: isCustomRange ? rangeRequest.startDate : undefined,
              endDate: isCustomRange ? rangeRequest.endDate : undefined,
              data: rangeData
            };
          })
        );

        console.log('✅ All date ranges fetched, sending back to AI...');

        // Make second call with the fetched data
        await handleSubmit(undefined, {
          originalPrompt: userMessage,
          fetchedDateRanges: fetchedData,
          aiMessage: data.message // Include any initial message from AI
        });
        
        return;
      }

      // Normal response with analysis
      if (data.message) {
        setConversation(prev => [...prev, { 
          role: 'assistant', 
          content: data.message,
          insights: data.insights 
        }]);
      }

    } catch (error: any) {
      console.error('Error analyzing dashboard:', error);
      setError(error.message || 'Failed to analyze dashboard');
      setConversation(prev => [...prev, { 
        role: 'assistant', 
        content: `Sorry, I encountered an error: ${error.message || 'Please try again.'}` 
      }]);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getInsightIcon = (type: 'positive' | 'warning' | 'negative') => {
    switch (type) {
      case 'positive':
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'negative':
        return <TrendingDown className="h-4 w-4 text-red-500" />;
    }
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const modalContent = (
    <div
      className="fixed inset-0 transition-opacity duration-200 bg-black/50"
      style={{
        zIndex: 100500,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={onClose}
    >
      <div
        className={`bg-[#1e1e1e] shadow-2xl flex flex-col overflow-hidden transform transition-all duration-200 ${
          isMobile
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[900px] lg:w-[1000px] ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100510,
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white px-6 py-6 relative flex items-center justify-between border-b border-blue-400/20">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">AI Business Analyzer</h2>
              <p className="text-sm text-blue-100 mt-0.5">
                Analyzing: {dashboardName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 flex items-center justify-center text-white/80 hover:text-white rounded-full hover:bg-white/10 active:bg-white/20 active:scale-95 transition-all duration-150"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col bg-[#1e1e1e]">
          {/* Conversation Area */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-[#2a2a2a]">
            {conversation.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                    message.role === 'user'
                      ? 'bg-blue-500 text-white'
                      : 'bg-[#3a3a3a] border border-gray-700 text-gray-100'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.content}</p>
                  
                  {/* Show insights if available */}
                  {message.insights && message.insights.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {message.insights.map((insight, i) => (
                        <div
                          key={i}
                          className={`flex items-start space-x-2 p-2 rounded-lg ${
                            insight.type === 'positive' ? 'bg-green-500/10 border border-green-500/20' :
                            insight.type === 'warning' ? 'bg-yellow-500/10 border border-yellow-500/20' :
                            'bg-red-500/10 border border-red-500/20'
                          }`}
                        >
                          {getInsightIcon(insight.type)}
                          <p className="text-xs text-gray-200 flex-1">{insight.message}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isAnalyzing && (
              <div className="flex justify-start">
                <div className="bg-[#3a3a3a] border border-gray-700 rounded-2xl px-4 py-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Error Display */}
          {error && (
            <div className="border-t border-gray-700 bg-red-900/20 px-6 py-3">
              <div className="flex items-center text-red-400 text-sm">
                <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                {error}
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className="border-t border-gray-700 bg-[#1e1e1e] p-4">
            <form onSubmit={handleSubmit} className="flex items-center space-x-3">
              <input
                ref={inputRef}
                type="text"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Ask me anything about your business data..."
                className="flex-1 px-4 py-3 bg-[#2a2a2a] border border-gray-700 text-gray-100 placeholder-gray-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                disabled={isAnalyzing}
              />
              <button
                type="submit"
                disabled={!prompt.trim() || isAnalyzing}
                className="p-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 active:scale-95 transition-all duration-150 disabled:bg-gray-700 disabled:cursor-not-allowed"
              >
                {isAnalyzing ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <Send className="h-5 w-5" />
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Token Purchase Modal */}
      {showTokenPurchaseModal && (
        <TokenPurchaseModal
          isOpen={showTokenPurchaseModal}
          onClose={() => setShowTokenPurchaseModal(false)}
          businessId={selectedBusiness?.id || ''}
        />
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
}

